import { Component, ElementRef, EventEmitter, forwardRef, inject, Input, Output, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { NOTIFICATIONS, NotificationsService } from 'src/app/core/service/notifications.service';
import { UploadService } from 'src/app/domain/shared/component/upload/service/upload.service';
import { AllIconsComponent } from "../all-icons/all-icons.component";
import { ERROR_IMAGE_LINK } from "../../../../util/common/links";

@Component({
  selector: 'app-upload',
  templateUrl: './upload.component.html',
  styleUrls: ['./upload.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => UploadComponent),
      multi: true,
    },
  ],
  imports: [
    AllIconsComponent,
  ],
})
export class UploadComponent {
  private uploadService = inject(UploadService);
  private notificationsService = inject(NotificationsService);

  @Input() oldImgSrc: string;
  @Input() widthHeight: string;
  @Input() placeholder: string = 'Upload avatar';
  @Output() deleteOldLink = new EventEmitter();
  @ViewChild('imageInput') imageInput: ElementRef;

  imgSnippet: ImageSnippet;
  errorImageLink = ERROR_IMAGE_LINK;
  inputSwitcher = true;

  emulateInputClick(): void {
    this.imageInput.nativeElement.click();
  }

  async upload(awsFolder: string, generatedImgName: string): Promise<string> {
    const { imgSnippet } = this;
    if (!imgSnippet) return null;
    const imgSnippetName = LATIN_REGEXP.test(imgSnippet.file.name) ?
      imgSnippet.file.name : this.replaceNonLatinWithRandomNumbers(imgSnippet.file.name);
    const imageName = generatedImgName + '/' + imgSnippetName;
    const uploadResult = await this.uploadService.uploadFile(imgSnippet.file, imageName, awsFolder);
    const awsImageName = `${AWS_BASE_URL}/${awsFolder}/${uploadResult.newFileName}`;
    return awsImageName;
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  processFile(imageInput: any): void {
    const file: File = imageInput.files[0];
    const reader = new FileReader();

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    reader.addEventListener('load', (event: any) => { // 'loadend'
      if (file.size > MAX_FILE_SIZE) {
        this.notificationsService.error(NOTIFICATIONS.PICTURE_SIZE_EXCEEDED_ERROR);
        return;
      }
      this.imgSnippet = new ImageSnippet(event.target.result, file);
      this.deleteOldLink.emit();
    });
    reader.readAsDataURL(file);
  }

  deleteClicked(): void {
    this.inputSwitcher = !this.inputSwitcher;
    this.imgSnippet = undefined;
    this.oldImgSrc = undefined;
    this.deleteOldLink.emit();
  }

  /**
   * Write form value to the DOM element (model => view)
   */
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  writeValue(value: any): void {
    this.oldImgSrc = value;
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  registerOnChange(fn: any): void {
    // Store the provided function and call it when the value changes
    this.onChange = fn;
  }

  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  registerOnTouched(fn: any): void {
    // Store the provided function and call it when the component is touched
    this.onTouched = fn;
  }

  // Helper methods to trigger the change and touched functions
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  onChange: any = () => { };
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  onTouched: any = () => { };

  // Example method that updates the value and triggers the change callback
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  updateValue(val: any): void {
    this.onChange(val);
    this.onTouched();
  }

  private replaceNonLatinWithRandomNumbers(str: string): string {
    const result = [];

    for (const char of str) {
      if (LATIN_REGEXP.test(char)) {
        result.push(char);
      } else {
        result.push(Math.floor(Math.random() * 10).toString());
      }
    }

    return result.join('');
  }
}

class ImageSnippet {
  constructor(public src: string, public file: File) { }
}

const MAX_FILE_SIZE = 5 * 1024 * 1024;

export const LATIN_REGEXP = /^[a-zA-Z0-9~`!@#\$%\^&\*\(\)_\-\+={\[\}\]\|\\:;"'<,>\.\?\/  ]*$/;
// TODO when localhost will be created -> make it dynamic
export const AWS_BASE_URL = `https://assets.${window.location.hostname}`;
